import { useDrawerContext } from "@/core/context/DrawerContext"
import ArrowStemLeftIcon from "@/core/ui/iconsax/linear/arrow-left.svg"
import useIsAdminViewingAsMember from "@/product/util/hook/useIsAdminViewingAsMember"
import useIsWebView from "@/product/util/hook/useIsWebView"
import { css, cssSafeValue } from "@assets/style/util/cssUtils"
import DiscoIconButton from "@disco-ui/button/DiscoIconButton"
import { Drawer, useTheme } from "@material-ui/core"
import { TestIDProps } from "@utils/typeUtils"
import { getWebViewHeader } from "@utils/webView/webViewUtils"
import classNames from "classnames"
import classes from "./DiscoDrawerSidebar.module.css"

interface DiscoDrawerSidebarProps extends TestIDProps {
  children: React.ReactNode
  classes?: {
    sidebar?: string
    mobileOverlayPaper?: string
  }
}

function DiscoDrawerSidebar({
  children,
  classes: customClasses,
}: DiscoDrawerSidebarProps) {
  const { isDrawerSidebarOpen, closeDrawerSidebar, isMobileWidth } = useDrawerContext()
  const isAdminViewingAs = useIsAdminViewingAsMember()
  const isWebView = useIsWebView()
  const webViewHeader = getWebViewHeader()

  const theme = useTheme()

  if (!isMobileWidth) return renderContent()

  return (
    <Drawer
      SlideProps={{
        timeout: 100,
        direction: "right",
      }}
      BackdropProps={{
        invisible: true,
      }}
      open={isDrawerSidebarOpen}
      onClose={closeDrawerSidebar}
      disableEnforceFocus
      classes={{
        paper: classNames(classes.mobileOverlayDrawer, customClasses?.mobileOverlayPaper),
      }}
      style={getDynamicStyles()}
      disablePortal={!isMobileWidth}
    >
      <DiscoIconButton
        className={classes.mobileOverlayDrawerCloseButton}
        onClick={closeDrawerSidebar}
        color={theme.palette.text.secondary}
      >
        <ArrowStemLeftIcon />
      </DiscoIconButton>
      {renderContent()}
    </Drawer>
  )

  function renderContent() {
    return (
      <div className={classNames(classes.sidebar, customClasses?.sidebar)}>
        {children}
      </div>
    )
  }

  function getDynamicStyles() {
    if (webViewHeader && isWebView) {
      return css({
        "--drawer-webview-header-height": cssSafeValue(webViewHeader.height),
        "--drawer-sidebar-margin-top": "var(--drawer-webview-header-height)",
        "--drawer-mobile-overlay-height": `calc(100% - var(--drawer-webview-header-height) + (var(--groovy-spacing) * 1.5))`,
        "--drawer-mobile-overlay-height-md-only": `calc(100% - var(--drawer-webview-header-height) + (var(--groovy-spacing) * 5))`,
      })
    }

    if (isAdminViewingAs && !isWebView) {
      return css({
        "--drawer-sidebar-margin-top": `calc(var(--groovy-spacing) * 10.5)`,
        "--drawer-mobile-overlay-height": `calc(100% - (var(--groovy-spacing) * 11))`,
        "--drawer-mobile-overlay-height-md-only": `calc(100% - (var(--groovy-spacing) * 13))`,
      })
    }

    return css({
      "--drawer-sidebar-margin-top": `calc(var(--groovy-spacing) * 0.5)`,
      "--drawer-mobile-overlay-height": `calc(100% - (var(--groovy-spacing) * 1.5))`,
      "--drawer-mobile-overlay-height-md-only": `calc(100% - (var(--groovy-spacing) * 5))`,
    })
  }
}

export default DiscoDrawerSidebar
